import { useMatch, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useGetAgentChatThread } from '@/entities/project/api/generated/api';
import { useProjectMemoStore } from '@/entities/project-memo/model/store';
import { type AIContext, useRouteSidePanelStore } from '@/shared/stores/side-panel-store';
import { CloseButton, DropdownMenuDeleteItem, Text } from '@/shared/ui';
import { useThreadActions } from '../../thread-actions/useThreadActions';
import type { ProjectDetailTab } from '../projectDetailTab';
import { ProjectDetailAIChatActionBar } from './ProjectDetailAIChatActionBar';
import { ProjectDetailAIChatTabs } from './ProjectDetailAIChatTabs';

type Props = {
  projectId: string;
  showHistory: boolean;
  setShowHistory: (show: boolean) => void;
  setAiContextMenu: (context: AIContext | undefined) => void;
  /** 現在表示中のスレッドID。スレッド未選択（welcome 画面）の場合は undefined */
  threadId: string | undefined;
  /** 現在選択中のタブ。1 段目の Tabs と 2 段目の表示有無を制御する */
  tab: ProjectDetailTab;
  /** タブ切替時のハンドラ。URL の `tab` search params を更新する */
  onTabChange: (next: ProjectDetailTab) => void;
};

export const ProjectDetailAIChatHeader = ({
  projectId,
  showHistory,
  setShowHistory,
  setAiContextMenu,
  threadId,
  tab,
  onTabChange,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // 現在ページが事業詳細・メモのどちらか判定する。閉じる/遷移先 URL を現在ページに合わせる。
  const isOnMemoPage = useMatch({
    from: '/_protected/projects/$projectId/memo',
    shouldThrow: false,
  });

  const requestClose = useRouteSidePanelStore((state) => state.requestClose);
  const setPendingThreadId = useProjectMemoStore((state) => state.setPendingThreadId);

  // 他 search params（tab / memoId）を保持したまま threadId だけクリアする
  const navigateRemovingThreadId = ({ replace = false }: { replace?: boolean } = {}) => {
    if (isOnMemoPage) {
      navigate({
        to: '/projects/$projectId/memo',
        params: { projectId },
        search: (prev) => ({ ...prev, threadId: undefined }),
        replace,
      });
    } else {
      navigate({
        to: '/projects/$projectId',
        params: { projectId },
        search: (prev) => ({ ...prev, threadId: undefined }),
        replace,
      });
    }
  };

  const handleClose = () => {
    requestClose();
    // 現在のページに留まり、URL の threadId のみ削除する（メモページでは memoId を保持）
    navigateRemovingThreadId();
  };

  // 「新しいチャット」: aiContextMenu / 履歴表示 / URL をすべて welcome 状態にリセットする。
  // ブラウザ履歴に「新しいチャット」状態を積まないため replace で遷移する（旧実装と同じ挙動）。
  const handleNewChat = () => {
    setAiContextMenu({ route: 'projectDetail', mode: 'agent_chat' });
    setShowHistory(false);
    navigateRemovingThreadId({ replace: true });
  };

  const handleCreateMemoFromThread = () => {
    if (!threadId) return;
    // メモページ側で creating モードに自動遷移させるための共有ストア
    setPendingThreadId(threadId);
    // 既にメモページにいる場合でも threadId クエリは維持して AI チャットを継続表示する
    navigate({
      to: '/projects/$projectId/memo',
      params: { projectId },
      search: (prev) => ({ ...prev, threadId }),
    });
  };

  // 現在スレッドのメタ情報（タイトル・お気に入り）を取得する。
  // スレッド総数が履歴一覧のページサイズを超えても確実にメニューを表示できるよう、
  // 個別取得 API を使う。`ProjectDetailAIChatContents` 側と同じクエリキーでキャッシュ共有される。
  const { data: currentThread } = useGetAgentChatThread(projectId, threadId ?? '', {
    query: { enabled: !!threadId },
  });

  const { openRename, openDelete, toggleFavorite, copyUrl, dialogs } = useThreadActions({
    projectId,
    onDeleteSuccess: () => {
      // 削除完了後は URL の threadId をクリアしてリロード時に削除済みスレッドを再取得しないようにする。
      // パネル内 aiContextMenu の threadId クリアは useThreadActions 側で済んでいる。
      navigateRemovingThreadId();
    },
  });

  // 1 段目: 左に Tabs（マッチング / チャット）、右に × 閉じる。高さは PageHeader と揃えて 49px。
  const topRow = (
    <div className="sticky top-0 flex h-[49px] shrink-0 items-center justify-between border-b bg-white px-4">
      <ProjectDetailAIChatTabs value={tab} onValueChange={onTabChange} />
      <CloseButton onClick={handleClose} aria-label={t('ai.common.close-panel')} />
    </div>
  );

  // マッチングタブでは 2 段目（タイトル + アクション）を表示しない
  if (tab === 'matching') {
    return topRow;
  }

  // 履歴一覧表示中: タイトル「履歴」 + + のみ（🕐 は現在地なので非表示、📖 / ⋮ はスレッド未選択のため非表示）
  if (showHistory) {
    return (
      <>
        {topRow}
        <ProjectDetailAIChatActionBar
          title={t('ai.chat.history-title')}
          onNewChat={handleNewChat}
        />
      </>
    );
  }

  // スレッド選択中: タイトル + [+ / 📖 / 🕐 / ⋮]
  if (currentThread) {
    const menuItems = [
      {
        key: 'favorite',
        item: (
          <Text>
            {currentThread.is_favorite
              ? t('ai.chat-history.menu-unfavorite')
              : t('ai.chat-history.menu-favorite')}
          </Text>
        ),
        onClick: () => toggleFavorite(currentThread),
      },
      {
        key: 'copy-url',
        item: <Text>{t('ai.chat.menu-copy-url')}</Text>,
        onClick: () => {
          void copyUrl(currentThread.id);
        },
      },
      {
        key: 'rename',
        item: <Text>{t('ai.chat-history.menu-rename')}</Text>,
        onClick: () => openRename(currentThread),
        hasBottomSeparator: true,
      },
      {
        key: 'delete',
        item: <DropdownMenuDeleteItem />,
        onClick: () => openDelete(currentThread),
      },
    ];

    return (
      <>
        {topRow}
        <ProjectDetailAIChatActionBar
          title={currentThread.title}
          onNewChat={handleNewChat}
          onShowHistory={() => setShowHistory(true)}
          onCreateMemo={handleCreateMemoFromThread}
          menuItems={menuItems}
        />
        {dialogs}
      </>
    );
  }

  // welcome 画面（スレッド未選択）: タイトルなし + 🕐 のみ
  return (
    <>
      {topRow}
      <ProjectDetailAIChatActionBar onShowHistory={() => setShowHistory(true)} />
    </>
  );
};
